/**
 * Notifications Resource
 *
 * Mute preferences for the Errors list. The inbox list/read/dismiss surface
 * was retired with Live Feed (feed ADR-006).
 */

import type { HttpLayer } from '../http.js';
import type {
  MutePrefsRequest,
  MutePrefsResponse,
  NotificationCursor,
  NotificationSubscribeResponse,
} from '../types/index.js';

export class NotificationsResource {
  constructor(private http: HttpLayer) {}

  /** Get the caller's own mute preferences. */
  async getPrefs(): Promise<MutePrefsResponse> {
    return this.http.get('/v1/notifications/prefs');
  }

  /** Replace the caller's own mute preferences. */
  async setPrefs(params: MutePrefsRequest): Promise<MutePrefsResponse> {
    return this.http.put('/v1/notifications/prefs', params);
  }

  /**
   * Subscribe a connection to the notification/error push stream + trigger a
   * reconnect gap-fill replay. Live delivery rides
   * AccountScope/UserScope fanout directly (no subscription row gates it); this
   * call (a) authorizes the caller-owned `deviceId` (403 on mismatch) and (b)
   * drains the durable backlog after `after` over the bridge as
   * `notification.events`/`error.events` frames. Omit `after` for a first
   * subscribe (full backlog); on reconnect pass the last `(created_at, id)`
   * cursor so the SDK's `event_id` dedup collapses any live/replay overlap.
   */
  async subscribe(
    deviceId: string,
    after?: NotificationCursor,
  ): Promise<NotificationSubscribeResponse> {
    return this.http.post('/v1/notifications/subscribe', {
      device_id: deviceId,
      ...(after ? { after } : {}),
    });
  }

  /** Unsubscribe a connection from the notification/error push stream. */
  async unsubscribe(deviceId: string): Promise<NotificationSubscribeResponse> {
    return this.http.post('/v1/notifications/unsubscribe', {
      device_id: deviceId,
    });
  }
}
